import { useState } from 'react';
import { Receipt as ReceiptIcon } from 'lucide-react';
import { useReceiptList } from './hooks';
import { documentLabel } from './kindLabel';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ListScreen, ListRow, FilterChip, type ListTab } from '../../components/ListScreen';

const TABS: ListTab[] = [
  { key: 'all', label: 'All' },
  { key: 'outstanding', label: 'Outstanding' },
  { key: 'paid', label: 'Paid' },
  { key: 'void', label: 'Void' }
];

const STATUS_BADGE: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600 border-slate-200',
  Sent: 'bg-slate-100 text-ink border-slate-200',
  Paid: 'bg-green-50 text-green-800 border-green-200',
  Void: 'bg-red-50 text-red-800 border-red-200'
};

function inTab(status: string, tab: string): boolean {
  if (tab === 'all') return true;
  if (tab === 'outstanding') return status === 'Draft' || status === 'Sent';
  if (tab === 'paid') return status === 'Paid';
  if (tab === 'void') return status === 'Void';
  return true;
}

export function ReceiptListPage() {
  usePageTitle('Receipts');
  const [tab, setTab] = useState('all');
  const [kind, setKind] = useState<'' | 'Deposit' | 'Final'>('');

  // Fetch all; filter status (tab) + kind (chip) client-side — the list endpoint
  // returns an array (no pagination), and B2 adds no API params.
  const { data, isLoading } = useReceiptList({});
  const rows = (data ?? []).filter((r) => inTab(r.status, tab) && (kind === '' || r.kind === kind));

  return (
    <ListScreen
      title="Receipts"
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      primaryAction={{ label: 'New Receipt', to: '/receipts/new-deposit' }}
      filters={
        <>
          <FilterChip active={kind === ''} onClick={() => setKind('')}>All types</FilterChip>
          <FilterChip active={kind === 'Deposit'} onClick={() => setKind('Deposit')}>Deposits</FilterChip>
          <FilterChip active={kind === 'Final'} onClick={() => setKind('Final')}>Receipts</FilterChip>
        </>
      }
      isEmpty={!isLoading && rows.length === 0}
      empty={{ icon: <ReceiptIcon size={40} />, message: 'No receipts here yet.' }}
    >
      {isLoading ? (
        <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>
      ) : (
        rows.map((r) => (
          <ListRow
            key={r.id}
            to={`/receipts/${r.id}`}
            primary={r.customerName}
            badge={
              <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] ${STATUS_BADGE[r.status] ?? ''}`}>
                {r.status}
              </span>
            }
            secondary={`${r.receiptNumber} · ${documentLabel(r.kind, r.status)}`}
            right={`${r.total.toFixed(2)} ${r.currency}`}
            rightSub={new Date(r.issuedAtUtc).toLocaleDateString()}
          />
        ))
      )}
    </ListScreen>
  );
}
